import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, X, CheckCircle2, Mail, Globe } from "lucide-react";
import {
  adminListDomainPolicies,
  adminUpsertDomainPolicy,
  adminDeleteDomainPolicy,
  adminListAccessRequests,
  adminUpdateAccessRequest,
} from "../../lib/admin";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "—";
  }
};

const PolicyForm = ({ initial, onSave, onCancel, busy }) => {
  const [domain, setDomain] = useState(initial?.domain || "");
  const [discount, setDiscount] = useState(initial?.discount_percent ?? 25);
  const [label, setLabel] = useState(initial?.label || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [active, setActive] = useState(initial?.active !== false);

  const submit = (e) => {
    e.preventDefault();
    const cleanDomain = (domain || "").trim().toLowerCase().replace(/^@/, "");
    if (!/^[a-z0-9.\-]+\.[a-z]{2,}$/.test(cleanDomain)) {
      toast.error("Domain must look like example.com (no @, no spaces).");
      return;
    }
    const d = parseInt(discount, 10);
    if (Number.isNaN(d) || d < 0 || d > 90) {
      toast.error("Discount must be a whole number between 0 and 90.");
      return;
    }
    onSave({
      domain: cleanDomain,
      discount_percent: d,
      label: label.trim() || null,
      notes: notes.trim() || null,
      active,
    });
  };

  return (
    <form
      onSubmit={submit}
      className="p-4 rounded-sm grid gap-3 sm:grid-cols-[1fr_120px_1fr_auto] items-end"
      style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }}
      data-testid="domain-policy-form"
    >
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Domain</span>
        <input
          type="text"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          placeholder="example.com"
          disabled={busy || Boolean(initial?.domain)}
          required
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm"
          data-testid="domain-policy-domain"
        />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Discount %</span>
        <input
          type="number"
          min={0}
          max={90}
          step={1}
          value={discount}
          onChange={(e) => setDiscount(e.target.value)}
          required
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm font-mono"
          data-testid="domain-policy-discount"
        />
      </label>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Label (optional)</span>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Acme Corp"
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm"
          data-testid="domain-policy-label"
        />
      </label>
      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy}
          className="btn-brutal px-3 py-2 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em]"
          data-testid="domain-policy-save"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : initial?.domain ? "Update" : "Add"}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-2 py-2 rounded-sm hover:bg-white"
            aria-label="Cancel"
            data-testid="domain-policy-cancel"
          >
            <X size={14} />
          </button>
        )}
      </div>
      <label className="sm:col-span-4 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          data-testid="domain-policy-active"
        />
        <span>Active (uncheck to temporarily disable without deleting)</span>
      </label>
      <label className="sm:col-span-4 block">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Notes (optional)</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm"
          placeholder="Internal note: e.g. signed contract Apr-2026"
          data-testid="domain-policy-notes"
        />
      </label>
    </form>
  );
};

const DomainsTab = () => {
  const [policies, setPolicies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // null = no edit, "new" = adding, {...} = editing existing

  const reload = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([adminListDomainPolicies(), adminListAccessRequests()]);
      setPolicies(p);
      setRequests(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const handleSave = async (payload) => {
    setBusy(true);
    try {
      await adminUpsertDomainPolicy(payload);
      toast.success(`${payload.domain} saved at ${payload.discount_percent}% off.`);
      setEditing(null);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (domain) => {
    if (!window.confirm(`Remove the whitelist entry for ${domain}? Existing sessions will keep working until they log out.`)) return;
    try {
      await adminDeleteDomainPolicy(domain);
      toast.success(`Removed ${domain}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const handleApprove = async (req) => {
    setEditing({ domain: req.domain, discount_percent: 25, label: req.company || "", notes: `Approved from access request by ${req.full_name} (${req.email})` });
    setTimeout(() => {
      const node = document.querySelector('[data-testid="domain-policy-form"]');
      node?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  const handleMarkRequest = async (id, status) => {
    try {
      await adminUpdateAccessRequest(id, status);
      toast.success(`Marked as ${status}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-[color:var(--ups-subtext)]" data-testid="domains-loading">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading whitelist…
      </div>
    );
  }

  return (
    <div className="space-y-10" data-testid="domains-tab">
      {/* Whitelist section */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-2xl font-bold flex items-center gap-2">
              <Globe size={20} /> Domain Whitelist
            </h3>
            <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
              Add a domain + discount %. Anyone with an email at that domain (or any subdomain) can sign in and gets that discount applied to every quote automatically. Whole numbers only (0-90).
            </p>
          </div>
          {!editing && (
            <button
              type="button"
              onClick={() => setEditing("new")}
              className="btn-brutal px-3 py-2 rounded-sm flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em]"
              data-testid="add-domain-btn"
            >
              <Plus size={14} /> Add Domain
            </button>
          )}
        </div>

        {editing === "new" && (
          <PolicyForm
            initial={null}
            busy={busy}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
          />
        )}
        {editing && typeof editing === "object" && (
          <PolicyForm
            initial={editing}
            busy={busy}
            onSave={handleSave}
            onCancel={() => setEditing(null)}
          />
        )}

        <div className="mt-5 rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)", background: "#fff" }}>
          {policies.length === 0 && (
            <div className="p-6 text-sm text-[color:var(--ups-subtext)]" data-testid="domains-empty">
              No whitelist entries yet — only the default <span className="font-mono">@theupsstore.com</span> partner is configured.
            </div>
          )}
          {policies.map((p, idx) => (
            <div
              key={p.domain}
              className="flex items-center justify-between gap-3 px-4 py-3"
              style={{ borderTop: idx === 0 ? "none" : "1px solid var(--ups-border)" }}
              data-testid={`domain-row-${p.domain}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{p.domain}</span>
                  {!p.active && (
                    <span
                      className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em]"
                      style={{ background: "#FEE2E2", color: "#991B1B" }}
                    >
                      Disabled
                    </span>
                  )}
                </div>
                {p.label && (
                  <div className="text-[12px] text-[color:var(--ups-subtext)] mt-0.5">{p.label}</div>
                )}
                {p.notes && (
                  <div className="text-[11px] font-mono text-[color:var(--ups-subtext)] mt-0.5 truncate">{p.notes}</div>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div
                  className="px-2.5 py-1 text-[12px] font-mono font-bold rounded-sm"
                  style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)", color: "var(--ups-accent)" }}
                  data-testid={`domain-discount-${p.domain}`}
                >
                  {p.discount_percent}% off
                </div>
                <button
                  type="button"
                  onClick={() => setEditing({ ...p })}
                  className="p-1.5 rounded-sm hover:bg-[color:var(--ups-muted)]"
                  aria-label="Edit"
                  data-testid={`edit-domain-${p.domain}`}
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(p.domain)}
                  className="p-1.5 rounded-sm hover:bg-[#FEE2E2]"
                  aria-label="Delete"
                  data-testid={`delete-domain-${p.domain}`}
                  disabled={p.domain === "theupsstore.com"}
                  title={p.domain === "theupsstore.com" ? "Default partner — cannot delete" : "Remove"}
                  style={{ opacity: p.domain === "theupsstore.com" ? 0.4 : 1 }}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Access Requests section */}
      <section>
        <div className="mb-4">
          <h3 className="font-display text-2xl font-bold flex items-center gap-2">
            <Mail size={20} /> Access Requests ({requests.length})
          </h3>
          <p className="text-sm text-[color:var(--ups-subtext)] mt-1">
            People who tried to log in with a non-whitelisted domain. Approve to pre-fill a whitelist entry — the team is also notified at <span className="font-mono">customerservice@printtales.com</span> for each request.
          </p>
        </div>
        <div className="rounded-sm overflow-hidden" style={{ border: "1px solid var(--ups-border)", background: "#fff" }}>
          {requests.length === 0 && (
            <div className="p-6 text-sm text-[color:var(--ups-subtext)]" data-testid="access-requests-empty">
              No access requests yet.
            </div>
          )}
          {requests.map((r, idx) => (
            <div
              key={r.id}
              className="flex items-start justify-between gap-4 px-4 py-3"
              style={{ borderTop: idx === 0 ? "none" : "1px solid var(--ups-border)" }}
              data-testid={`access-request-${r.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-sm">{r.full_name}</span>
                  <span className="font-mono text-[12px] text-[color:var(--ups-subtext)]">&lt;{r.email}&gt;</span>
                  <span
                    className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em]"
                    style={{
                      background: r.status === "approved" ? "#DCFCE7" : r.status === "rejected" ? "#FEE2E2" : "#FEF3C7",
                      color: r.status === "approved" ? "#166534" : r.status === "rejected" ? "#991B1B" : "#92400E",
                    }}
                  >
                    {r.status}
                  </span>
                </div>
                <div className="text-[12px] text-[color:var(--ups-subtext)] mt-0.5">
                  domain <span className="font-mono">{r.domain}</span>
                  {r.company ? <> · {r.company}</> : null}
                  <> · {fmtDate(r.created_at)}</>
                </div>
                {r.use_case && (
                  <div className="text-[12px] mt-1.5 text-[color:var(--ups-ink)] leading-snug">&ldquo;{r.use_case}&rdquo;</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.status === "pending" && (
                  <>
                    <button
                      type="button"
                      onClick={() => { handleApprove(r); }}
                      className="btn-brutal px-2.5 py-1.5 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5"
                      data-testid={`access-approve-${r.id}`}
                    >
                      <CheckCircle2 size={12} /> Whitelist
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMarkRequest(r.id, "rejected")}
                      className="px-2.5 py-1.5 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] hover:bg-[#FEE2E2]"
                      style={{ border: "1px solid var(--ups-border)" }}
                      data-testid={`access-reject-${r.id}`}
                    >
                      Reject
                    </button>
                  </>
                )}
                {r.status !== "pending" && (
                  <button
                    type="button"
                    onClick={() => handleMarkRequest(r.id, "pending")}
                    className="px-2.5 py-1.5 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] hover:bg-[color:var(--ups-muted)]"
                    style={{ border: "1px solid var(--ups-border)" }}
                    data-testid={`access-reopen-${r.id}`}
                  >
                    Re-open
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DomainsTab;
